// Renders a photo that may be a lazy token — fetches the real image only when
// it scrolls into view, so photo-heavy inspections open instantly.
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Image as ImageIcon } from "lucide-react";
import { isLazyPhoto, resolveLazyPhoto } from "@/lib/lazyPhotos";

export const LazyImg = ({ src, inspectionId, alt = "", className = "", ...rest }) => {
  const { id: routeId } = useParams();
  const iid = inspectionId || routeId;
  const lazy = isLazyPhoto(src);
  const [real, setReal] = useState(lazy ? null : src);
  const [failed, setFailed] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!lazy) { setReal(src); return undefined; }
    setReal(null);
    setFailed(false);
    const el = ref.current;
    let cancelled = false;
    const load = () => resolveLazyPhoto(iid, src)
      .then((p) => { if (!cancelled) setReal(p); })
      .catch(() => { if (!cancelled) setFailed(true); });
    if (!el || typeof IntersectionObserver === "undefined") { load(); return () => { cancelled = true; }; }
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { obs.disconnect(); load(); }
    }, { rootMargin: "300px" });
    obs.observe(el);
    return () => { cancelled = true; obs.disconnect(); };
  }, [src, iid, lazy]);

  if (real) {
    return <img src={real} alt={alt} loading="lazy" decoding="async" className={className} {...rest} />;
  }
  return (
    <div ref={ref} className={`${className} bg-slate-100 flex items-center justify-center`}
      data-testid="lazy-photo-placeholder" {...rest}>
      {failed
        ? <span className="text-[10px] text-slate-400 font-bold text-center px-1">Photo available online</span>
        : <ImageIcon size={18} className="text-slate-300 animate-pulse" />}
    </div>
  );
};

export default LazyImg;
